import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, GraduationCap } from 'lucide-react';
import AccountMenu from './AccountMenu';

interface AppNavBarProps {
  // Brand text next to the logo, e.g. "Teacher Console"
  title: string;
  icon?: React.ReactNode;
  // Under the user's name; defaults to the role
  roleLabel?: string;
  // Turns the brand into a "Back to Dashboard" button (lesson page)
  backToDashboard?: boolean;
  // Shown after the brand, e.g. the lesson title
  subtitle?: React.ReactNode;
  // Extra controls before the account menu
  actions?: React.ReactNode;
  fullWidth?: boolean;
}

// The top bar shared by the dashboards and the lesson page: brand, page actions and the account menu (time left, theme,
// settings, Logout).
const AppNavBar: React.FC<AppNavBarProps> = ({ title, icon, roleLabel, backToDashboard = false, subtitle, actions, fullWidth = false }) => {
  const navigate = useNavigate();
  const brandIcon = (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm [&_svg]:w-5 [&_svg]:h-5">
      {icon ?? <GraduationCap />}
    </span>
  );

  const brand = (
    <span className="flex items-center gap-2.5 min-w-0">
      {brandIcon}
      <span className={`min-w-0 text-left leading-tight ${subtitle ? 'hidden md:block' : 'hidden min-[360px]:block'}`}>
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Webots Academy</span>
        <span className="block text-base font-semibold text-gray-900 dark:text-white truncate">{title}</span>
      </span>
    </span>
  );

  return (
    <nav className="bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40 flex-shrink-0 transition-colors duration-200">
      <div className={`${fullWidth ? 'max-w-full' : 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div className="flex justify-between items-center h-16 gap-3">
          {backToDashboard ? (
            // Inside a lesson: a plain way back and the lesson's name, nothing else (the owner's design, 2026-10-25).
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="group -ml-1.5 flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                aria-label="Back to my lessons"
              >
                <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                <span className="hidden sm:inline">Lessons</span>
              </button>
              {subtitle && (
                <>
                  <div className="h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                  {/* Not clipped here: the lesson page truncates its title itself, beside its readiness badge (LES-18). */}
                  <div className="min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100">{subtitle}</div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              {brand}
              {subtitle && (
                <>
                  <div className="h-6 w-px bg-gray-200 dark:bg-gray-800 shrink-0 hidden md:block" />
                  <div className="min-w-0 text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">{subtitle}</div>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {actions}
            {actions && <div className="h-6 w-px bg-gray-200 dark:bg-gray-800 hidden sm:block" />}
            <AccountMenu roleLabel={roleLabel} />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AppNavBar;
